import React, { useEffect, useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { colors } from 'src/components/app-ui'
import URL from 'src/api/url/api'

const TEXT_SIZES = [
  { key: 's', label: 'S' },
  { key: 'm', label: 'M' },
  { key: 'l', label: 'L' },
]

/**
 * Name and link font sizes (px) for a text size, as the learner app draws
 * them. M is the size the portal had before the setting.
 */
const SCALE = {
  header: { s: [13, 12], m: [15, 14], l: [17, 16] },
  footer: { s: [12, 11], m: [14, 13], l: [16, 15] },
}
export const textScale = (kind, size) => {
  const [name, link] = SCALE[kind][size || 'm'] || SCALE[kind].m
  return { name, link }
}

/** Logo height (px) when none was set. */
export const DEFAULT_LOGO_SIZE = { header: 40, footer: 22 }
const MIN_LOGO_SIZE = 16
const MAX_LOGO_SIZE = 56

export const mediaUrl = path =>
  !path ? '' : path.startsWith('http') ? path : `${URL.MEDIA}${path}`

const useStyles = makeStyles({
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  label: {
    fontSize: '14px',
    fontWeight: 500,
    color: colors.navy,
  },
  range: {
    width: '100%',
    accentColor: colors.primary,
    cursor: 'pointer',
  },
  segmented: {
    alignSelf: 'flex-start',
    display: 'flex',
    gap: '2px',
    padding: '3px',
    borderRadius: '8px',
    background: colors.hover,
  },
  segment: {
    height: 30,
    minWidth: 36,
    padding: '0 10px',
    border: 0,
    borderRadius: '6px',
    background: 'transparent',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '13px',
    fontWeight: 600,
    color: colors.muted,
  },
  segmentOn: {
    background: '#FFFFFF',
    boxShadow: '0 1px 2px rgba(0,0,0,.08)',
    color: colors.navy,
  },
})

/**
 * Saved when the slider is let go, not on every step; `onDraft` gets every
 * step so the preview follows the slider.
 */
const LogoSizeField = ({ value, onChange, onDraft }) => {
  const classes = useStyles()
  const [size, setSize] = useState(value)

  useEffect(() => setSize(value), [value])

  const commit = () => size !== value && onChange(size)

  return (
    <div className={classes.field}>
      <div className={classes.label}>Logo size</div>
      <input
        type="range"
        min={MIN_LOGO_SIZE}
        max={MAX_LOGO_SIZE}
        step={2}
        value={size}
        className={classes.range}
        onChange={event => {
          const next = Number(event.target.value)
          setSize(next)
          onDraft(next)
        }}
        onMouseUp={commit}
        onTouchEnd={commit}
        onKeyUp={commit}
      />
    </div>
  )
}

/**
 * Portal › Navigation / Footer: the logo size and text size of the header or
 * footer (the logo is the workspace logo, Workspace › Branding). `kind` is
 * "header" or "footer"; the portal fields are `${kind}LogoSize` and
 * `${kind}TextSize`. `onDraft` gets unsaved slider values
 * for the preview.
 */
const BrandFields = ({ kind, portal, onChange, onDraft = () => {} }) => {
  const classes = useStyles()

  const logoSize = portal[`${kind}LogoSize`] || DEFAULT_LOGO_SIZE[kind]
  const textSize = portal[`${kind}TextSize`] || 'm'

  return (
    <>
      <LogoSizeField
        value={logoSize}
        onChange={value => onChange({ [`${kind}LogoSize`]: value })}
        onDraft={value => onDraft({ [`${kind}LogoSize`]: value })}
      />
      <div className={classes.field}>
        <span className={classes.label}>Text size</span>
        <div className={classes.segmented}>
          {TEXT_SIZES.map(size => (
            <button
              key={size.key}
              type="button"
              className={`${classes.segment} ${
                size.key === textSize ? classes.segmentOn : ''
              }`}
              onClick={() =>
                size.key !== textSize &&
                onChange({ [`${kind}TextSize`]: size.key })
              }
            >
              {size.label}
            </button>
          ))}
        </div>
      </div>
    </>
  )
}

export default BrandFields
